import React, { useCallback, useEffect, useRef, useState } from 'react';
import confetti from 'canvas-confetti';
import './Reina.css';

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

function WaveOnceText({
  children,
  amplitude = 10,
  speed = 5,
  bounces = 4,
  delayPerLetter = 0.04,
  className = '',
}) {
  const [time, setTime] = useState(0);
  const frameRef = useRef(null);

  useEffect(() => {
    let startTime;
    const totalWave = (Math.PI * bounces) / speed;
    const totalDuration = totalWave + (children.length - 1) * delayPerLetter + 0.2;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsed = (timestamp - startTime) / 1000;
      setTime(elapsed);
      if (elapsed < totalDuration) {
        frameRef.current = requestAnimationFrame(animate);
      }
    };

    frameRef.current = requestAnimationFrame(animate);
    return () => {
      if (frameRef.current) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, [children, bounces, speed, delayPerLetter]);

  const totalWave = (Math.PI * bounces) / speed;

  return (
    <div className={`reina-wave ${className}`.trim()}>
      {children.split('').map((char, i) => {
        const delay = i * delayPerLetter;
        const localTime = Math.max(0, time - delay);
        let y = 0;

        if (localTime <= totalWave) {
          const bounceTime = localTime * speed;
          y = amplitude * Math.sin(bounceTime);
        }

        return (
          <span
            key={`${char}-${i}`}
            className="reina-wave-letter"
            style={{ transform: `translateY(${-y}px)` }}
          >
            {char}
          </span>
        );
      })}
    </div>
  );
}

function useWindowSize() {
  const getSize = () => {
    const styles = getComputedStyle(document.documentElement);
    let availableWidth = parseInt(styles.getPropertyValue('--available-width'), 10);
    let availableHeight = parseInt(styles.getPropertyValue('--available-height'), 10);

    if (Number.isNaN(availableWidth) || availableWidth === 0) {
      availableWidth = window.innerWidth;
    }
    if (Number.isNaN(availableHeight) || availableHeight === 0) {
      availableHeight = window.innerHeight;
    }

    return {
      width: window.innerWidth,
      height: window.innerHeight,
      dwidth: availableWidth,
      dheight: availableHeight,
    };
  };

  const [windowSize, setWindowSize] = useState(getSize);

  useEffect(() => {
    const initialTimeout = setTimeout(() => {
      setWindowSize(getSize());
    }, 50);

    const handleResize = () => {
      setTimeout(() => {
        setWindowSize(getSize());
      }, 10);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      clearTimeout(initialTimeout);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  return windowSize;
}

function Reina() {
  const containerRef = useRef(null);
  const noButtonRef = useRef(null);
  const yesButtonRef = useRef(null);
  const initialNoRectRef = useRef(null);
  const { dwidth, dheight } = useWindowSize();
  const typeIntervalRef = useRef(null);
  const typeTimeoutsRef = useRef([]);
  const lastNoEventRef = useRef(0);
  const [accepted, setAccepted] = useState(false);
  const [noPos, setNoPos] = useState({ x: 0, y: 0 });
  const [noReady, setNoReady] = useState(false);
  const [noFloating, setNoFloating] = useState(false);
  const [noClickCount, setNoClickCount] = useState(0);
  const [noDismissed, setNoDismissed] = useState(false);
  const [typedText, setTypedText] = useState('');
  const [typingDone, setTypingDone] = useState(false);
  const [yayText, setYayText] = useState('');
  const [yayDone, setYayDone] = useState(false);
  const [showGif, setShowGif] = useState(false);
  const [showLove, setShowLove] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [showYes, setShowYes] = useState(false);
  const [showNo, setShowNo] = useState(false);
  const [showHint, setShowHint] = useState(false);

  const setRandomNoPosition = useCallback(() => {
    const padding = 16;
    const availableWidth = dwidth || window.innerWidth;
    const availableHeight = dheight || window.innerHeight;
    const rect = noButtonRef.current?.getBoundingClientRect();
    const buttonWidth = rect?.width ?? 120;
    const buttonHeight = rect?.height ?? 44;
    const safeTop = Math.min(160, availableHeight * 0.25);

    const maxX = Math.max(padding, availableWidth - buttonWidth - padding);
    const maxY = Math.max(safeTop, availableHeight - buttonHeight - padding);

    const yesRect = yesButtonRef.current?.getBoundingClientRect();
    const initialNoRect = initialNoRectRef.current;
    const avoidPadding = 24;

    const expandRect = (rect, extra) => {
      if (!rect) return null;
      return {
        left: rect.left - extra,
        top: rect.top - extra,
        right: rect.right + extra,
        bottom: rect.bottom + extra,
      };
    };

    const avoidRects = [expandRect(yesRect, avoidPadding), expandRect(initialNoRect, 12)].filter(Boolean);

    const isOverlapping = (x, y) => avoidRects.some((rect) => (
      x < rect.right &&
      x + buttonWidth > rect.left &&
      y < rect.bottom &&
      y + buttonHeight > rect.top
    ));

    let x = padding;
    let y = safeTop;
    let attempts = 0;
    const maxAttempts = 25;

    while (attempts < maxAttempts) {
      x = Math.floor(Math.random() * (maxX - padding + 1)) + padding;
      y = Math.floor(Math.random() * (maxY - safeTop + 1)) + safeTop;
      if (!isOverlapping(x, y)) {
        break;
      }
      attempts += 1;
    }

    setNoPos({ x, y });
  }, [dwidth, dheight]);

  useEffect(() => {
    if (accepted) {
      return;
    }

    const fullText = 'reina, will you be my valentine?';
    const pauseAt = 'reina, '.length;
    setTypedText('');
    setTypingDone(false);
    setShowYes(false);
    setShowNo(false);
    setShowHint(false);

    if (typeIntervalRef.current) {
      clearInterval(typeIntervalRef.current);
    }
    typeTimeoutsRef.current.forEach(clearTimeout);
    typeTimeoutsRef.current = [];

    let index = 0;
    typeIntervalRef.current = setInterval(() => {
      index += 1;
      setTypedText(fullText.slice(0, index));
      if (index === pauseAt) {
        clearInterval(typeIntervalRef.current);
        typeIntervalRef.current = null;
        typeTimeoutsRef.current.push(setTimeout(() => {
          typeIntervalRef.current = setInterval(() => {
            index += 1;
            setTypedText(fullText.slice(0, index));
            if (index >= fullText.length) {
              clearInterval(typeIntervalRef.current);
              typeIntervalRef.current = null;
              setTypingDone(true);

              typeTimeoutsRef.current.push(setTimeout(() => setShowYes(true), 200));
              typeTimeoutsRef.current.push(setTimeout(() => setShowNo(true), 550));
              typeTimeoutsRef.current.push(setTimeout(() => setShowHint(true), 1550));
            }
          }, 60);
        }, 350));
        return;
      }
      if (index >= fullText.length) {
        clearInterval(typeIntervalRef.current);
        typeIntervalRef.current = null;
        setTypingDone(true);

        typeTimeoutsRef.current.push(setTimeout(() => setShowYes(true), 200));
        typeTimeoutsRef.current.push(setTimeout(() => setShowNo(true), 550));
        typeTimeoutsRef.current.push(setTimeout(() => setShowHint(true), 1550));
      }
    }, 60);

    return () => {
      if (typeIntervalRef.current) {
        clearInterval(typeIntervalRef.current);
        typeIntervalRef.current = null;
      }
      typeTimeoutsRef.current.forEach(clearTimeout);
      typeTimeoutsRef.current = [];
    };
  }, [accepted]);

  useEffect(() => {
    if (!noFloating && noButtonRef.current) {
      initialNoRectRef.current = noButtonRef.current.getBoundingClientRect();
    }
  }, [noFloating]);

  useEffect(() => {
    if (!noFloating) {
      return undefined;
    }

    const padding = 16;

    const handleResize = () => {
      const resizedRect = noButtonRef.current?.getBoundingClientRect();
      if (!resizedRect) {
        return;
      }

      const availableWidth = dwidth || window.innerWidth;
      const availableHeight = dheight || window.innerHeight;
      const maxX = availableWidth - resizedRect.width - padding;
      const maxY = availableHeight - resizedRect.height - padding;

      setNoPos((prev) => ({
        x: clamp(prev.x, padding, maxX),
        y: clamp(prev.y, padding, maxY),
      }));
    };

    setRandomNoPosition();
    setNoReady(true);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [noFloating, setRandomNoPosition, dwidth, dheight]);

  useEffect(() => {
    if (!accepted) {
      setYayText('');
      setYayDone(false);
      setShowGif(false);
      setShowLove(false);
      setShowReset(false);
      return;
    }

    const fullText = '♥♥ YAYY!! ♥♥';
    setYayText('');
    setYayDone(false);

    let index = 0;
    const intervalId = setInterval(() => {
      index += 1;
      setYayText(fullText.slice(0, index));
      if (index >= fullText.length) {
        clearInterval(intervalId);
        setYayDone(true);
      }
    }, 70);

    return () => clearInterval(intervalId);
  }, [accepted]);

  useEffect(() => {
    if (!accepted || !yayDone) {
      return undefined;
    }

    setShowGif(true);
    const loveTimeout = setTimeout(() => setShowLove(true), 1000);
    const resetTimeout = setTimeout(() => setShowReset(true), 2000);

    return () => {
      clearTimeout(loveTimeout);
      clearTimeout(resetTimeout);
    };
  }, [accepted, yayDone]);

  useEffect(() => {
    if (!accepted) {
      return;
    }

    const end = Date.now() + 1400;

    const frame = () => {
      confetti({
        particleCount: 4,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0.6 },
      });
      confetti({
        particleCount: 4,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0.6 },
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };

    frame();
  }, [accepted]);

  const handleNoMove = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (noDismissed) {
      return;
    }

    const now = Date.now();
    if (now - lastNoEventRef.current > 180) {
      lastNoEventRef.current = now;
      setNoClickCount((prev) => {
        const next = prev + 1;
        if (next >= 10) {
          setNoDismissed(true);
          setNoFloating(false);
          setNoReady(false);
        }
        return next;
      });
    }
    if (!noFloating) {
      setNoFloating(true);
      return;
    }
    setRandomNoPosition();
  };

  const handleYes = () => {
    setAccepted(true);
  };

  const handleReset = () => {
    setAccepted(false);
    setNoFloating(false);
    setNoReady(false);
    setNoClickCount(0);
    setNoDismissed(false);
  };

  const handleCelebrationClick = (event) => {
    if (!accepted) {
      return;
    }

    const { clientX, clientY } = event;
    const originX = clientX / window.innerWidth;
    const originY = clientY / window.innerHeight;

    confetti({
      particleCount: 120,
      spread: 70,
      startVelocity: 35,
      origin: { x: originX, y: originY },
    });

  };

  return (
    <div
      className="reina-page"
      onClick={handleCelebrationClick}
    >
      <div className="reina-card" ref={containerRef}>
        {!accepted && (
          <h1 className="reina-question reina-mono">
            <span className="reina-typewriter">{typedText}</span>
            {!typingDone && <span className="reina-caret" aria-hidden="true">▍</span>}
          </h1>
        )}

        {!accepted && (
          <>
            <div className="reina-button-row">
              {showYes && (
                <button
                  type="button"
                  className="reina-button reina-yes reina-mono reina-fade-in"
                  ref={yesButtonRef}
                  onClick={handleYes}
                >
                  <strong>yes</strong> 🤍
                </button>
              )}
              {showNo && !noFloating && !noDismissed && (
                <button
                  ref={noButtonRef}
                  type="button"
                  className="reina-button reina-no-inline reina-mono reina-fade-in"
                  aria-label={`no (${noClickCount})`}
                  onPointerEnter={handleNoMove}
                  onPointerDown={handleNoMove}
                  onFocus={handleNoMove}
                >
                  no 🥀
                </button>
              )}
            </div>

            {showHint && (
              <p className="reina-hint reina-fade-in">
                {noDismissed ? "i'll make it super easy for you" : 'the no button is a little shy :)'}
              </p>
            )}
          </>
        )}

        {accepted && (
          <div className="reina-result">
            <div className="reina-yay reina-mono">
              <span className="reina-typewriter">
                {yayText.split('').map((char, index) => (
                  <span
                    key={`${char}-${index}`}
                    className={char === '♥' ? 'reina-yay-heart' : undefined}
                  >
                    {char}
                  </span>
                ))}
              </span>
              {!yayDone && <span className="reina-caret" aria-hidden="true">▍</span>}
            </div>
            {showGif && (
              <img
                src="/snoopy-vday.gif"
                alt="snoopy valentine"
                className="reina-gif reina-fade-in"
              />
            )}
            {showLove && (
              <WaveOnceText className="reina-love reina-fade-in">
                Love&nbsp;u&nbsp;too!!
              </WaveOnceText>
            )}
            {showReset && (
              <button
                type="button"
                className="reina-reset reina-fade-in"
                onClick={handleReset}
                aria-label="Reset"
                title="Reset"
              >
                ↺
              </button>
            )}
          </div>
        )}
      </div>

      {!accepted && (
        <>
          {showNo && noFloating && !noDismissed && (
            <button
              ref={noButtonRef}
              type="button"
              className={`reina-button reina-no reina-mono${noReady ? '' : ' reina-no-hidden'}`}
              style={{ left: `${noPos.x}px`, top: `${noPos.y}px` }}
              aria-label={`no (${noClickCount})`}
              onPointerEnter={handleNoMove}
              onPointerDown={handleNoMove}
              onFocus={handleNoMove}
            >
              no 🥀
            </button>
          )}
        </>
      )}
    </div>
  );
}

export default Reina;
